<template>
  <div class="layout-fullscreen layout-fullscreen--overlay">
    <div class="layout-fullscreen__container">
      <HeaderBar :modifiers="['transparent']">
        <div class="header-bar__icon">
          <button
            class="button button--transparent"
            @click="cancel"
            :aria-label="$t('general.cancel')"
          >
            <CloseSvg />
          </button>
        </div>
        <div class="header-bar__title typo-title">
          {{ $t('operations.edit_bounds') }}
        </div>

        <template #right>
          <button class="button button--inverse" @click="save">
            {{ $t('general.done') }}
          </button>
        </template>
      </HeaderBar>

      <HeaderBar :modifiers="['light', 'border-bottom']">
        <Typeahead
          class="place-search"
          v-model="place"
          :resultsFn="fetchResults"
          :placeholder="$t('operations.find_location')"
        />

        <div class="header-bar__separator"></div>

        <div class="radio-button radio-button--icon-only">
          <input
            class="radio-button__input"
            id="tool_hand"
            type="radio"
            value="hand"
            v-model="tool"
            aria-label="Hand tool"
          />
          <label class="radio-button__content" for="tool_hand">
            <Hand32Svg class="radio-button__icon" />
          </label>
        </div>
        <div class="radio-button">
          <input
            class="radio-button__input"
            id="tool_define_bounds"
            type="radio"
            value="define_bounds"
            v-model="tool"
            :disabled="hasPolygon"
          />
          <label class="radio-button__content" for="tool_define_bounds">
            <DefineBoundsSvg class="radio-button__icon" />
            <span class="radio-button__label">{{ $t('operations.define_bounds') }}</span>
          </label>
        </div>

        <div class="header-bar__separator"></div>

        <div :class="{ 'dropdown': true, 'dropdown--disabled': !hasPolygon }">
          <Popper placement="bottom-end" v-model:open="deleteMenuOpen">
            <div class="menu menu--dropdown">
              <ul class="menu__items">
                <li class="menu-item menu-item--dropdown">
                  <button class="menu-item__link" @click="clearBounds" :disabled="!hasPolygon">
                    <span class="menu-item__label">{{ $t('operations.clear_bounds') }}</span>
                  </button>
                </li>
              </ul>
            </div>

            <template #trigger>
              <div class="select select--input select--icon">
                <div class="select__trigger" aria-label="Delete menu">
                  <span class="select__label-icons">
                    <div class="select__label-icon">
                      <Trash32Svg />
                    </div>
                  </span>

                  <span class="select__arrow">
                    <UseSvg name="arrow_down_16" class="icon icon--small" />
                  </span>
                </div>
              </div>
            </template>
          </Popper>
        </div>

        <template #right>
          <UndoRedo
            ref="undoRedo"
            v-if="!loading"
            :map="map"
            :getState="getState"
            @update="setState"
          />
        </template>
      </HeaderBar>

      <LeafletMap
        ref="map"
        :overlays="overlays"
        :editable="true"
        :defaultPolygonOptions="defaultPolygonOptions"
        @init="onMapInit"
        @polygonComplete="polygonComplete"
      />

      <div class="place-popup" v-if="place">
        <MapPinSvg class="place-popup__icon" />
        <div class="place-popup__title typo-tiny">
          {{ place.title }}
        </div>
        <button
          class="button button--transparent button--tiny place-popup__close-button"
          type="button"
          @click="place = null"
        >
          <CloseCircle16Svg />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watchEffect } from 'vue';

import deepCloneLatLngs from '@/functions/deep_clone_lat_lngs';
import { leafletPolygonToRGeo, rgeoToPolygon } from '@/functions/geospatial_conversions';
import onEscapePressed from '@/functions/on_escape_pressed';

import findPlaces from '@/vendor/find_places';
import { mapMarkerIconPin } from '@/vendor/leaflet_mods';
import LeafletMap from '@/vendor/components/leaflet_map.vue';

import Typeahead from '@/components/form/typeahead.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Popper from '@/components/generic/popper.vue';
import UndoRedo from '@/components/mapping/undo_redo.vue';

import CloseSvg from '@/images/close.svg';
import Trash32Svg from '@/images/trash-32.svg';
import Hand32Svg from '@/images/hand-32.svg';
import DefineBoundsSvg from '@/images/define-bounds.svg';
import MapPinSvg from '@/images/map-pin.svg';
import CloseCircle16Svg from '@/images/close-circle-16.svg';

const defaultPolygonOptions = {
  className: 'region-polygon region-polygon--operation'
};

const props = defineProps({
  overlays: { type: Array, default: () => [] },
  modelValue: { type: String, default: null },
});
const emit = defineEmits(['update:modelValue', 'close']);

const map = ref(null);
const undoRedo = ref(null);
const loading = ref(true);
const tool = ref('hand');
const deleteMenuOpen = ref(false);
const place = ref(null);

let polygon = null;
const hasPolygon = ref(false);

onEscapePressed(event => {
  if (map.value.isDrawing()) {
    tool.value = 'hand';
    event.preventDefault();
  }
});

watchEffect(() => {
  if (loading.value) return;
  switch (tool.value) {
    case 'define_bounds':
      map.value.startPoly();
      break;
    case 'hand':
      map.value.stopDrawing();
      break;
  }
});

watchEffect(() => {
  if (!map.value) return;

  if (place.value) {
    flyToPlace();
  } else {
    map.value.clearMarkers();
  }
});

function onMapInit() {
  loadExistingBounds();
  loading.value = false;
}

function loadExistingBounds() {
  if (props.modelValue) {
    createPolygon(rgeoToPolygon(props.modelValue));
    map.value.goToObject(polygon);
  } else {
    removePolygon();
  }
}

function polygonComplete(newPolygon) {
  tool.value = 'hand';
  setPolygon(newPolygon);
  undoRedo.value.pushUndo();
}

function getState() {
  if (polygon) {
    return deepCloneLatLngs(polygon.getLatLngs())
  } else {
    return [];
  }
}

function setState(latLngs) {
  if (latLngs.length === 0) {
    removePolygon();
  } else {
    if (polygon) {
      polygon.setLatLngs(deepCloneLatLngs(latLngs));
      polygon.editor.reset();
    } else {
      createPolygon(latLngs);
    }
  }
}

function clearBounds() {
  if (polygon) {
    removePolygon();
    undoRedo.value.pushUndo();
  }
  deleteMenuOpen.value = false;
}

function removePolygon() {
  if (polygon) {
    map.value.removeObject(polygon);
    setPolygon(null);
  }
}

function createPolygon(latLngs) {
  setPolygon(map.value.addPolygon(latLngs));
  polygon.enableEdit();
}

function setPolygon(poly) {
  polygon = poly;
  hasPolygon.value = !!poly;
}

function save() {
  let newModelValue = polygon ?
    leafletPolygonToRGeo(polygon.getLatLngs()) :
    null;
  emit('update:modelValue', newModelValue);
  emit('close');
}

function cancel() {
  emit('close');
}

function fetchResults(search) {
  return findPlaces(search, map.value.getBounds());
}

function flyToPlace() {
  let { mapView, position: { lat, lng } } = place.value;
  if (mapView) {
    map.value.flyToBounds(mapView.south, mapView.west, mapView.north, mapView.east);
  } else {
    map.value.flyTo([lat, lng], 18);
  }

  map.value.addMarker([lat, lng], { icon: mapMarkerIconPin });
}
</script>
